import { z } from 'zod';
import { cantonFixSourceSchema, cantonFixStatusSchema, chargeFailureCategorySchema, chargeFailureStatusSchema, klaviyoSubscriberCategorySchema, klaviyoSyncActionSchema, legacyMigrationStatusSchema, rechargeFeatureModeSchema, rechargeFeatureSchema, rechargeRunStatusSchema, rechargeTopicSchema, rechargeWebhookOriginSchema, rechargeWebhookOutcomeSchema, } from '../status/recharge.js';
import { renewalDiscountFeatureModeSchema } from '../status/renewal-discount.js';
import { dateRangeQuerySchema, isoDateTimeSchema, objectIdSchema, paginationQuerySchema, sortQuerySchema, } from './common.js';
/**
 * API des abonnements Recharge — `06` §7 (réglages), §1.3 (webhooks
 * enregistrés), §8.5 (journal des webhooks reçus).
 */
/** Format du canton écrit sur une adresse suisse (`06` §3.1). */
export const SWISS_CANTON_FORMATS = ['name', 'code'];
export const swissCantonFormatSchema = z.enum(SWISS_CANTON_FORMATS);
export const rechargeChargeFailedSettingsSchema = z.object({
    mode: rechargeFeatureModeSchema,
    /** « M » de « Tentative N/M » : Recharge n'expose pas ce maximum (`06` §2.1). */
    maxAttempts: z.number().int().min(1).max(20),
});
export const rechargeSwissCantonSettingsSchema = z.object({
    mode: rechargeFeatureModeSchema,
    provinceFormat: swissCantonFormatSchema,
});
export const rechargeLegacyMigrationSettingsSchema = z.object({
    mode: rechargeFeatureModeSchema,
});
/** Remise renouvellement (`07` §7) : la réconciliation quotidienne et son seuil d'alerte. */
export const rechargeRenewalDiscountSettingsSchema = z.object({
    /** Désactivé ou Actif : pas de simulation pour cette fonction (`07` §7). */
    mode: renewalDiscountFeatureModeSchema,
    /** Expression cron (Europe/Paris) de la réconciliation. */
    reconcileSchedule: z.string().trim().min(1).max(100),
    /** Nombre de remises perdues ou en erreur, par passe, à partir duquel Flux alerte. */
    alertThreshold: z.number().int().min(1).max(10_000),
});
/** Synchronisation Klaviyo (`06` §11) : un mode, sans topic Recharge ; la clé est dans l'intégration Klaviyo. */
export const rechargeKlaviyoSettingsSchema = z
    .object({
    mode: rechargeFeatureModeSchema,
})
    .strict();
export const rechargeFeatureSettingsSchema = z.object({
    chargeFailed: rechargeChargeFailedSettingsSchema,
    swissCanton: rechargeSwissCantonSettingsSchema,
    legacyMigration: rechargeLegacyMigrationSettingsSchema,
    renewalDiscount: rechargeRenewalDiscountSettingsSchema,
});
/** `GET /api/recharge/settings` : les secrets ne sont jamais renvoyés, seulement leur présence. */
export const rechargeSettingsSummarySchema = z.object({
    apiTokenConfigured: z.boolean(),
    webhookSecretConfigured: z.boolean(),
    slackWebhookConfigured: z.boolean(),
    /** URL à enregistrer chez Recharge (`APP_URL` + `/webhooks/recharge`). */
    webhookUrl: z.string(),
    /** Mode test global : force la simulation de toutes les fonctions (`06` §0). */
    testMode: z.boolean(),
    features: rechargeFeatureSettingsSchema,
    klaviyo: rechargeKlaviyoSettingsSchema,
});
const secretInputSchema = z.string().trim().min(1).max(2_000);
/**
 * `PUT /api/recharge/settings` — mise à jour partielle : chaque carte de
 * l'écran n'envoie que sa section. Un secret absent n'est pas modifié.
 */
export const rechargeSettingsUpdateBodySchema = z
    .object({
    apiToken: secretInputSchema.optional(),
    webhookSecret: secretInputSchema.optional(),
    slackWebhookUrl: z.url({ protocol: /^https$/ }).optional(),
    chargeFailed: rechargeChargeFailedSettingsSchema.optional(),
    swissCanton: rechargeSwissCantonSettingsSchema.optional(),
    legacyMigration: rechargeLegacyMigrationSettingsSchema.optional(),
    renewalDiscount: rechargeRenewalDiscountSettingsSchema.optional(),
    klaviyo: rechargeKlaviyoSettingsSchema.optional(),
})
    .strict();
/** Un webhook enregistré sur le compte Recharge (`GET /webhooks`, `06` §1.3). */
export const rechargeRegisteredWebhookSchema = z.object({
    id: z.number().int(),
    topic: z.string(),
    address: z.string(),
    origin: rechargeWebhookOriginSchema,
});
export const rechargeRegisteredWebhooksSchema = z.object({
    items: z.array(rechargeRegisteredWebhookSchema),
    /** Topics des fonctions non désactivées qui ne pointent pas encore vers Flux. */
    missingTopics: z.array(rechargeTopicSchema),
});
export const rechargeRegisterWebhooksResultSchema = z.object({
    created: z.array(rechargeTopicSchema),
    webhooks: rechargeRegisteredWebhooksSchema,
});
/** Élément métier produit par le traitement d'un webhook (lien depuis le journal). */
export const rechargeWebhookRefSchema = z.object({
    feature: rechargeFeatureSchema,
    id: objectIdSchema,
});
export const rechargeWebhookEventListItemSchema = z.object({
    id: objectIdSchema,
    receivedAt: isoDateTimeSchema,
    topic: z.string(),
    /** Type de l'objet Recharge (`charge`, `address`, `subscription`), `null` si le payload n'en porte pas. */
    objectType: z.string().nullable(),
    objectId: z.string().nullable(),
    feature: rechargeFeatureSchema.nullable(),
    outcome: rechargeWebhookOutcomeSchema,
    reason: z.string().nullable(),
    mode: rechargeFeatureModeSchema.nullable(),
    processedAt: isoDateTimeSchema.nullable(),
    ref: rechargeWebhookRefSchema.nullable(),
});
export const rechargeWebhookEventDetailSchema = rechargeWebhookEventListItemSchema.extend({
    payload: z.record(z.string(), z.unknown()),
});
export const rechargeWebhookEventListQuerySchema = z.object({
    ...paginationQuerySchema.shape,
    ...sortQuerySchema.shape,
    ...dateRangeQuerySchema.shape,
    topic: z.array(z.string().min(1)).optional(),
    outcome: z.array(rechargeWebhookOutcomeSchema).optional(),
});
/** Une génération de l'export des abonnements actifs (`06` §4, écran `06` §8.4). */
export const rechargeExportListItemSchema = z.object({
    id: objectIdSchema,
    status: rechargeRunStatusSchema,
    startedAt: isoDateTimeSchema,
    finishedAt: isoDateTimeSchema.nullable(),
    startedBy: z.object({ id: objectIdSchema, name: z.string() }).nullable(),
    subscriptions: z.number().int().min(0).nullable(),
    rows: z.number().int().min(0).nullable(),
    missingShopifyTitles: z.number().int().min(0).nullable(),
    fileName: z.string().nullable(),
    /** `false` quand le contenu a été purgé après `csvRetentionDays`, ou pas encore produit. */
    downloadable: z.boolean(),
    error: z.string().nullable(),
});
export const rechargeExportListQuerySchema = z.object({
    ...paginationQuerySchema.shape,
});
/** Trace du message Slack abonnements d'un élément (`06` §1.4). */
export const rechargeSlackTraceSchema = z.object({
    preview: z.string(),
    sentAt: isoDateTimeSchema.nullable(),
    error: z.string().nullable(),
});
/** Paiement échoué d'un renouvellement (`06` §2, écran `06` §8.1). */
export const chargeFailureListItemSchema = z.object({
    id: objectIdSchema,
    receivedAt: isoDateTimeSchema,
    chargeId: z.string(),
    customerId: z.string().nullable(),
    email: z.string(),
    amount: z.string().nullable(),
    errorType: z.string().nullable(),
    errorMessage: z.string().nullable(),
    category: chargeFailureCategorySchema,
    attempt: z.number().int().min(0),
    maxAttempts: z.number().int().min(1),
    retryDate: z.string().nullable(),
    status: chargeFailureStatusSchema,
    mode: rechargeFeatureModeSchema,
});
export const chargeFailureDetailSchema = chargeFailureListItemSchema.extend({
    products: z.array(z.object({ title: z.string(), quantity: z.number() })),
    subscriptionIds: z.array(z.string()),
    /** La charge n'a pas pu être relue chez Recharge : données du payload seules. */
    refetchFailed: z.boolean(),
    slack: rechargeSlackTraceSchema,
});
export const chargeFailureListQuerySchema = z.object({
    ...paginationQuerySchema.shape,
    ...sortQuerySchema.shape,
    ...dateRangeQuerySchema.shape,
    category: z.array(chargeFailureCategorySchema).optional(),
    status: z.array(chargeFailureStatusSchema).optional(),
    search: z.string().trim().min(1).max(200).optional(),
});
/** Correction de canton d'une adresse suisse (`06` §3, écran `06` §8.2). */
export const cantonFixListItemSchema = z.object({
    id: objectIdSchema,
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
    addressId: z.string(),
    customerId: z.string().nullable(),
    address1: z.string().nullable(),
    zip: z.string().nullable(),
    city: z.string().nullable(),
    /** Canton écrit, ou qui l'aurait été ; `null` si non résolu. */
    province: z.string().nullable(),
    status: cantonFixStatusSchema,
    source: cantonFixSourceSchema,
    reason: z.string().nullable(),
    candidates: z.array(z.string()),
    mode: rechargeFeatureModeSchema,
});
export const cantonFixListQuerySchema = z.object({
    ...paginationQuerySchema.shape,
    ...sortQuerySchema.shape,
    ...dateRangeQuerySchema.shape,
    status: z.array(cantonFixStatusSchema).optional(),
});
/** Vérification de toutes les adresses suisses (`06` §3.3). */
export const cantonScanSchema = z.object({
    id: objectIdSchema,
    status: rechargeRunStatusSchema,
    mode: z.enum(['simulation', 'active']),
    startedAt: isoDateTimeSchema,
    finishedAt: isoDateTimeSchema.nullable(),
    startedBy: z.object({ id: objectIdSchema, name: z.string() }).nullable(),
    /** `read`, `swiss`, `updated`, `simulated`, `skipped`, `unresolved`, `errors`. */
    counts: z.record(z.string(), z.number()),
    /** Formats de canton déjà présents sur les adresses CH : valeur → nombre. */
    provinceFormats: z.record(z.string(), z.number()),
    error: z.string().nullable(),
});
export const cantonScanStartBodySchema = z.object({ simulation: z.boolean() }).strict();
/** Dernière vérification et nombre d'adresses à traiter, en tête de l'écran. */
export const cantonFixesOverviewSchema = z.object({
    unresolvedCount: z.number().int().min(0),
    lastScan: cantonScanSchema.nullable(),
});
/** Migration d'un abonnement legacy (`06` §5, écran `06` §8.3). */
export const legacyMigrationListItemSchema = z.object({
    id: objectIdSchema,
    createdAt: isoDateTimeSchema,
    subscriptionId: z.string(),
    customerId: z.string().nullable(),
    email: z.string().nullable(),
    status: legacyMigrationStatusSchema,
    reason: z.string().nullable(),
    /** Nombre de produits du bundle (hors produits supprimés). */
    productCount: z.number().int().min(0),
    legacyPrice: z.number().nullable(),
    adjustedLegacyPrice: z.number().nullable(),
    newPrice: z.number().nullable(),
    difference: z.number().nullable(),
    bundle: z.object({ id: z.number(), name: z.string(), percent: z.number() }).nullable(),
    /** Prix des items restés faux après 3 corrections (`06` §5.3). */
    priceWarning: z.boolean(),
    mode: rechargeFeatureModeSchema,
});
export const legacyMigrationDetailSchema = legacyMigrationListItemSchema.extend({
    composition: z.string().nullable(),
    items: z.array(z.object({
        alias: z.string(),
        product: z.string(),
        title: z.string(),
        quantity: z.number(),
        /** Prix catalogue Shopify × 0,9 ; `null` pour un produit supprimé. */
        unitPrice: z.number().nullable(),
        deleted: z.boolean(),
    })),
    totalAfterSubscriptionDiscount: z.number().nullable(),
    bundleSelectionId: z.string().nullable(),
    priceCheck: z.object({ attempts: z.number().int(), ok: z.boolean() }).nullable(),
    failedStep: z.string().nullable(),
    /** L'abonnement d'origine est sauvegardé et la migration peut être annulée depuis l'écran (`06` §5.6). */
    rollbackAvailable: z.boolean(),
    rolledBackAt: isoDateTimeSchema.nullable(),
    rolledBackBy: z.object({ id: objectIdSchema, name: z.string() }).nullable(),
    slack: rechargeSlackTraceSchema.nullable(),
});
export const legacyMigrationListQuerySchema = z.object({
    ...paginationQuerySchema.shape,
    ...sortQuerySchema.shape,
    ...dateRangeQuerySchema.shape,
    status: z.array(legacyMigrationStatusSchema).optional(),
    search: z.string().trim().min(1).max(200).optional(),
});
/** Un profil Klaviyo modifié (ou qui l'aurait été) par une synchronisation. */
export const klaviyoSyncChangeSchema = z.object({
    action: klaviyoSyncActionSchema,
    email: z.string().nullable(),
    profileId: z.string().nullable(),
    /** Valeur lue sur le profil avant la synchronisation ; `null` si absente. */
    from: z.string().nullable(),
    to: klaviyoSubscriberCategorySchema.nullable(),
    /** Refus de Klaviyo sur ce profil ; la synchronisation continue. */
    error: z.string().nullable(),
});
/** Une synchronisation Klaviyo (`06` §11), écran Abonnements → Klaviyo. */
export const klaviyoSyncListItemSchema = z.object({
    id: objectIdSchema,
    status: rechargeRunStatusSchema,
    mode: z.enum(['simulation', 'active']),
    trigger: z.enum(['schedule', 'manual']),
    startedAt: isoDateTimeSchema,
    finishedAt: isoDateTimeSchema.nullable(),
    startedBy: z.object({ id: objectIdSchema, name: z.string() }).nullable(),
    /**
     * `subscribers`, `legacy`, `legacyNew`, `new`, `missingEmail`, `tagged`,
     * `unchanged`, `set`, `unset`, `errors`.
     */
    counts: z.record(z.string(), z.number()),
    error: z.string().nullable(),
});
export const klaviyoSyncDetailSchema = klaviyoSyncListItemSchema.extend({
    changes: z.array(klaviyoSyncChangeSchema),
    /** Changements au-delà de la limite conservée sur la synchronisation. */
    changesTruncated: z.number().int().min(0),
});
export const klaviyoSyncListQuerySchema = z.object({
    ...paginationQuerySchema.shape,
});
export const klaviyoSyncStartBodySchema = z.object({ simulation: z.boolean() }).strict();
//# sourceMappingURL=recharge.js.map